import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Users, AlertCircle, CheckCircle, Clock, Euro } from "lucide-react";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [abteilungen, setAbteilungen] = useState([]);
  const [wachen, setWachen] = useState([]);
  const [filterWache, setFilterWache] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  // Helper to ensure we always have an array
  const toArray = (data) => Array.isArray(data) ? data : [];

  useEffect(() => {
    loadData();
  }, [filterWache, filterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, abtRes, wachenRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/abteilungen-with-status`, {
          params: {
            wache_id: filterWache !== "all" ? filterWache : undefined,
            status_filter: filterStatus !== "all" ? filterStatus : undefined,
          },
        }),
        axios.get(`${API}/wachen`),
      ]);

      setStats(statsRes.data);
      setAbteilungen(abtRes.data);
      setWachen(wachenRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "overdue":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "paid":
        return "Bezahlt";
      case "pending":
        return "Ausstehend";
      case "overdue":
        return "Überfällig";
      default:
        return status;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE");
  };

  const getIntervallText = (intervall) => {
    switch (intervall) {
      case "monthly":
        return "Monatlich";
      case "quarterly":
        return "Vierteljährlich";
      case "half-yearly":
        return "Halbjährlich";
      case "yearly":
        return "Jährlich";
      default:
        return intervall;
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="dashboard-container">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600">Übersicht über alle Zahlungen und Wachen</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="card-white card-hover" data-testid="stat-wachen">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Wachen</CardTitle>
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.anzahl_wachen}</div>
          </CardContent>
        </Card>

        <Card className="card-white card-hover" data-testid="stat-abteilungen">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Abteilungen</CardTitle>
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.anzahl_abteilungen}</div>
          </CardContent>
        </Card>

        <Card className="card-white card-hover" data-testid="stat-offene">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Offene</CardTitle>
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.offene_zahlungen_anzahl}</div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">{(stats.offene_zahlungen_betrag || 0).toFixed(2)} €</p>
          </CardContent>
        </Card>

        <Card className="card-white card-hover" data-testid="stat-ueberfaellig">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Überfällig</CardTitle>
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-red-600">{stats.ueberfaellige_zahlungen_anzahl}</div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">{(stats.ueberfaellige_zahlungen_betrag || 0).toFixed(2)} €</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-white">
        <CardHeader>
          <CardTitle className="text-gray-900">Zahlungsübersicht</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Select value={filterWache} onValueChange={setFilterWache}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="filter-wache">
                  <SelectValue placeholder="Nach Wache filtern" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  <SelectItem value="all">Alle Wachen</SelectItem>
                  {toArray(wachen).map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="filter-status">
                  <SelectValue placeholder="Nach Status filtern" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-300">
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="overdue">Überfällig</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="paid">Bezahlt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Abteilungen List */}
          <div className="space-y-3">
            {abteilungen.length === 0 ? (
              <div className="text-center py-8 text-gray-600">Keine Abteilungen gefunden</div>
            ) : (
              toArray(abteilungen).map((abt) => (
                <div
                  key={abt.abteilung_id}
                  className="p-3 sm:p-4 rounded-lg bg-white border border-gray-200 hover:border-gray-300 transition-colors"
                  data-testid={`abteilung-item-${abt.abteilung_id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm sm:text-base text-gray-900">{abt.abteilung_name}</h3>
                        <span className={`status-badge status-${abt.status} text-xs`}>
                          {getStatusIcon(abt.status)}
                          {getStatusText(abt.status)}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600">{abt.wache_name}</p>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-lg sm:text-xl font-bold text-gray-900">{(abt.betrag || 0).toFixed(2)} €</div>
                      <div className="text-xs sm:text-sm text-gray-600">{getIntervallText(abt.intervall)}</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm">
                    <div className="text-gray-600">
                      {abt.letzte_zahlung ? (
                        <span>Letzte: {formatDate(abt.letzte_zahlung)}</span>
                      ) : (
                        <span>Noch keine Zahlung</span>
                      )}
                    </div>
                    <div className="text-gray-700">
                      Fällig: {formatDate(abt.naechste_zahlung_faellig)}
                      {abt.tage_bis_faellig < 0 ? (
                        <span className="text-red-600 ml-2">({Math.abs(abt.tage_bis_faellig)}d überfällig)</span>
                      ) : (
                        <span className="text-gray-600 ml-2">(in {abt.tage_bis_faellig}d)</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;